import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { stationLabel } from '@jobshop/shared';
import type {
  Appearance,
  AppearanceWrite,
  ArchiveFinishedResult,
  Batch,
  BatchCreate,
  BatchPatch,
  BoardResponse,
  CalendarException,
  CalendarExceptionWrite,
  JobCreate,
  JobDetail,
  JobNoteCreate,
  JobNotePatch,
  JobPatch,
  JobStatus,
  JobSummary,
  JobTemplate,
  JobTemplateWrite,
  LabelSheet,
  ListItem,
  ListItemCreate,
  ListItemMarkCreate,
  ListItemPatch,
  MeResponse,
  PartCreate,
  PartPatch,
  PartsResponse,
  PlanMove,
  PlanMoveResult,
  ScanCreate,
  ScanResult,
  ScansResponse,
  ScheduleResult,
  SchedulingSettings,
  SchedulingSettingsWrite,
  Station,
  StationKey,
  StepStatus,
  User,
  UserWrite,
  Worker,
  WorkerWrite,
} from '@jobshop/shared';
import { del, get, patch, post, postForm, put } from './client';

/** Typed hooks over the API. Shapes come from `@jobshop/shared`, not from guesswork. */

export const keys = {
  me: ['me'] as const,
  jobs: (status?: JobStatus, archived?: boolean) =>
    ['jobs', archived ? 'archived' : 'live', status ?? 'all'] as const,
  job: (id: string) => ['job', id] as const,
  jobScans: (id: string) => ['job', id, 'scans'] as const,
  recentScans: (limit: number) => ['scans', 'recent', limit] as const,
  jobListItems: (id: string) => ['job', id, 'list-items'] as const,
  board: (from: string, days: number) => ['board', from, days] as const,
  parts: (batchId: string) => ['parts', batchId] as const,
  labels: (batchId: string) => ['labels', batchId] as const,
  stations: ['stations'] as const,
  workers: ['workers'] as const,
  exceptions: ['calendar-exceptions'] as const,
  templates: ['job-templates'] as const,
  users: ['users'] as const,
  appearance: ['appearance'] as const,
  schedulingSettings: ['scheduling-settings'] as const,
};

export function useMe(enabled: boolean) {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => get<MeResponse>('/me'),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** Live jobs by default; `archived: true` reads the archive instead of them. */
export function useJobs({ status, archived }: { status?: JobStatus; archived?: boolean } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (archived) params.set('archived', 'true');
  const query = params.toString();
  return useQuery({
    queryKey: keys.jobs(status, archived),
    queryFn: () => get<JobSummary[]>(`/jobs${query ? `?${query}` : ''}`),
  });
}

export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: keys.job(id ?? ''),
    queryFn: () => get<JobDetail>(`/jobs/${id}`),
    enabled: !!id,
  });
}

export function useJobScans(id: string | undefined) {
  return useQuery({
    queryKey: keys.jobScans(id ?? ''),
    queryFn: () => get<ScansResponse>(`/jobs/${id}/scans`),
    enabled: !!id,
  });
}

/**
 * The whole shop's latest scans. Kept out of `jobScans` on purpose: this one is not
 * about a job, and invalidating a job's history must not blow this away, or the Scans
 * page would refetch every time somebody deleted a scan on an unrelated job.
 */
export function useRecentScans(limit = 40) {
  return useQuery({
    queryKey: keys.recentScans(limit),
    queryFn: () => get<ScansResponse>(`/scans/recent?limit=${limit}`),
  });
}

export function useJobListItems(id: string | undefined) {
  return useQuery({
    queryKey: keys.jobListItems(id ?? ''),
    queryFn: () => get<ListItem[]>(`/jobs/${id}/list-items`),
    enabled: !!id,
  });
}

export function useBoard(from: string, days: number) {
  return useQuery({
    queryKey: keys.board(from, days),
    queryFn: () => get<BoardResponse>(`/schedule?from=${from}&days=${days}`),
    placeholderData: (prev) => prev,
  });
}

export function useParts(batchId: string | undefined) {
  return useQuery({
    queryKey: keys.parts(batchId ?? ''),
    queryFn: () => get<PartsResponse>(`/batches/${batchId}/parts`),
    enabled: !!batchId,
  });
}

export function useLabels(batchId: string | undefined) {
  return useQuery({
    queryKey: keys.labels(batchId ?? ''),
    queryFn: () => get<LabelSheet>(`/batches/${batchId}/labels`),
    enabled: !!batchId,
  });
}

function config<T>(path: string, key: readonly unknown[], extra?: Partial<UseQueryOptions<T>>) {
  return { queryKey: key, queryFn: () => get<T>(path), staleTime: 5 * 60_000, ...extra };
}

export const useStations = () => useQuery(config<Station[]>('/stations', keys.stations));

/**
 * Stations that take part in the plan. Every picker that feeds scheduling — a routing,
 * a worker, a calendar exception — uses this; the Scan page deliberately uses
 * `useStations` instead, because scan-only stations are exactly what it is for.
 */
export function useSchedulableStations(): Station[] {
  const stations = useStations();
  return (stations.data ?? []).filter((s) => !s.scanOnly);
}

/**
 * Resolves a station key to the name the shop gave it. Every screen showing a station
 * goes through this, so renaming one in Admin renames it everywhere — the alternative,
 * deriving text from the key, quietly ignores the shop's own naming.
 *
 * Falls back to the key for a station that no longer exists but is still referenced by
 * an old booking or scan.
 */
export function useStationName(): (key: string) => string {
  const stations = useStations();
  const byKey = new Map((stations.data ?? []).map((s) => [s.key as string, s.name]));
  return (key: string) => byKey.get(key) ?? stationLabel(key);
}

export const useWorkers = () => useQuery(config<Worker[]>('/workers', keys.workers));
export const useExceptions = () =>
  useQuery(config<CalendarException[]>('/calendar-exceptions', keys.exceptions));
export const useTemplates = () => useQuery(config<JobTemplate[]>('/job-templates', keys.templates));
/** `enabled` defaults to true for admin-panel callers; the app shell passes whether a
 * session exists yet, so this is not fetched — and does not 401 — before sign-in. */
export const useAppearance = (enabled = true) =>
  useQuery({ ...config<Appearance>('/appearance', keys.appearance), enabled });
export const useSchedulingSettings = () =>
  useQuery(config<SchedulingSettings>('/scheduling-settings', keys.schedulingSettings));

/**
 * The shop's configured timezone, for "today" on every screen — the board, job
 * dates, calendar exceptions. Falls back to the viewer's own device only for the
 * instant before the first fetch resolves; it is not a per-viewer choice.
 */
export function useShopTimeZone(): string {
  const appearance = useAppearance();
  return appearance.data?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
}
export const useUsers = (enabled: boolean) =>
  useQuery({ ...config<User[]>('/users', keys.users), enabled });

/** Invalidating broadly is right here: a re-plan can move any job on any board page. */
function useInvalidateAll() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['job'] });
    void qc.invalidateQueries({ queryKey: ['jobs'] });
    void qc.invalidateQueries({ queryKey: ['board'] });
  };
}

export function useCreateJob() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (input: JobCreate) => post<JobDetail>('/jobs', input),
    onSuccess: invalidate,
  });
}

export function usePatchJob(id: string) {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (input: JobPatch) => patch<JobDetail>(`/jobs/${id}`, input),
    onSuccess: invalidate,
  });
}

/** Archives every done/cancelled job at once. The list is what changed — nothing on
 * the board moves, because archiving leaves bookings alone. */
export function useArchiveFinishedJobs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<ArchiveFinishedResult>('/jobs/archive-finished'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<void>(`/jobs/${id}`),
    onSuccess: () => {
      // Deliberately not the usual broad invalidation: `['job']` would match the job
      // just deleted and refetch it into a 404 while its page is still mounted. The
      // list and the board are what actually changed — the freed capacity lands there.
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      void qc.invalidateQueries({ queryKey: ['board'] });
    },
  });
}

/**
 * Notes ride along on the job document, so all three mutations just re-fetch the job.
 * Nothing about a note can move a booking, so the board is left alone.
 */
function useJobInvalidation(jobId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.job(jobId) });
    void qc.invalidateQueries({ queryKey: ['jobs'] });
  };
}

export function useAddJobNote(jobId: string) {
  return useMutation({
    mutationFn: (input: JobNoteCreate) => post<JobDetail>(`/jobs/${jobId}/notes`, input),
    onSuccess: useJobInvalidation(jobId),
  });
}

export function usePatchJobNote(jobId: string) {
  return useMutation({
    mutationFn: ({ noteId, input }: { noteId: string; input: JobNotePatch }) =>
      patch<JobDetail>(`/jobs/${jobId}/notes/${noteId}`, input),
    onSuccess: useJobInvalidation(jobId),
  });
}

export function useDeleteJobNote(jobId: string) {
  return useMutation({
    mutationFn: (noteId: string) => del<void>(`/jobs/${jobId}/notes/${noteId}`),
    onSuccess: useJobInvalidation(jobId),
  });
}

/** Reference attachments for the job as a whole — ride on the job document like
 * notes, shown at the bottom of the List page. */
export function useAddJobAttachment(jobId: string) {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return postForm<JobDetail>(`/jobs/${jobId}/attachments`, form);
    },
    onSuccess: useJobInvalidation(jobId),
  });
}

export function useDeleteJobAttachment(jobId: string) {
  return useMutation({
    mutationFn: (attachmentId: string) => del<JobDetail>(`/jobs/${jobId}/attachments/${attachmentId}`),
    onSuccess: useJobInvalidation(jobId),
  });
}

/**
 * List items ride on their own collection, not the job document, so mutations
 * invalidate their own key plus the jobs list (for the open-item count) — never the
 * job or the board, since nothing here can move a booking.
 */
function useListItemInvalidation(jobId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.jobListItems(jobId) });
    void qc.invalidateQueries({ queryKey: ['jobs'] });
  };
}

/**
 * Deleting a scan touches the job's derived plan (parts-confirmed counts, slippage
 * badges) as well as its own history, so both the scans list and the job detail need
 * to be re-fetched — plus the jobs list, since badges surface there too.
 */
export function useDeleteScan(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scanId: string) => del<void>(`/jobs/${jobId}/scans/${scanId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.jobScans(jobId) });
      void qc.invalidateQueries({ queryKey: keys.job(jobId) });
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      void qc.invalidateQueries({ queryKey: ['scans', 'recent'] });
    },
  });
}

export function useAddListItem(jobId: string) {
  return useMutation({
    mutationFn: (input: ListItemCreate) => post<ListItem>(`/jobs/${jobId}/list-items`, input),
    onSuccess: useListItemInvalidation(jobId),
  });
}

export function usePatchListItem(jobId: string) {
  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: ListItemPatch }) =>
      patch<ListItem>(`/jobs/${jobId}/list-items/${itemId}`, input),
    onSuccess: useListItemInvalidation(jobId),
  });
}

export function useDeleteListItem(jobId: string) {
  return useMutation({
    mutationFn: (itemId: string) => del<void>(`/jobs/${jobId}/list-items/${itemId}`),
    onSuccess: useListItemInvalidation(jobId),
  });
}

/** A row's "special items" — added and deleted, never edited (see `listItemMark`). */
export function useAddListItemMark(jobId: string) {
  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: ListItemMarkCreate }) =>
      post<ListItem>(`/jobs/${jobId}/list-items/${itemId}/marks`, input),
    onSuccess: useListItemInvalidation(jobId),
  });
}

export function useDeleteListItemMark(jobId: string) {
  return useMutation({
    mutationFn: ({ itemId, markId }: { itemId: string; markId: string }) =>
      del<ListItem>(`/jobs/${jobId}/list-items/${itemId}/marks/${markId}`),
    onSuccess: useListItemInvalidation(jobId),
  });
}

export function useAddAttachment(jobId: string) {
  return useMutation({
    mutationFn: ({ itemId, file }: { itemId: string; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      return postForm<ListItem>(`/jobs/${jobId}/list-items/${itemId}/attachments`, form);
    },
    onSuccess: useListItemInvalidation(jobId),
  });
}

export function useDeleteAttachment(jobId: string) {
  return useMutation({
    mutationFn: ({ itemId, attachmentId }: { itemId: string; attachmentId: string }) =>
      del<ListItem>(`/jobs/${jobId}/list-items/${itemId}/attachments/${attachmentId}`),
    onSuccess: useListItemInvalidation(jobId),
  });
}

export function useAddBatch(jobId: string) {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (input: BatchCreate) => post(`/jobs/${jobId}/batches`, input),
    onSuccess: invalidate,
  });
}

export function usePatchBatch() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: BatchPatch }) =>
      patch(`/batches/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteBatch() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: string) => del<void>(`/batches/${id}`),
    onSuccess: invalidate,
  });
}

export function useScheduleJob() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (jobId: string) => post<ScheduleResult[]>(`/jobs/${jobId}/schedule`),
    onSuccess: invalidate,
  });
}

export function useScheduleBatch() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (batchId: string) => post<ScheduleResult>(`/batches/${batchId}/schedule`),
    onSuccess: invalidate,
  });
}

export function useUnscheduleBatch() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (batchId: string) => del<{ removed: number }>(`/batches/${batchId}/schedule`),
    onSuccess: invalidate,
  });
}

export function useReplanAll() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: () => post<ScheduleResult[]>('/schedule/replan'),
    onSuccess: invalidate,
  });
}

/**
 * One drag on the board. Everything from the grabbed chip onward moves; everything
 * before it stays. Re-plans that batch only — the shop is not reshuffled because
 * somebody dragged one job.
 */
export function useMovePlan() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ batchId, ...move }: PlanMove & { batchId: string }) =>
      post<PlanMoveResult>(`/batches/${batchId}/plan/move`, move),
    onSuccess: invalidate,
  });
}

/** Takes the last drag back. Restores the constraint and re-plans, not the bookings. */
export function useUndoPlanMove() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (batchId: string) => post<ScheduleResult>(`/batches/${batchId}/plan/undo`),
    onSuccess: invalidate,
  });
}

/** Drops every hand-move on a batch. A date fixed on purpose survives. */
export function useReleasePlanMoves() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (batchId: string) => del<ScheduleResult>(`/batches/${batchId}/plan/moves`),
    onSuccess: invalidate,
  });
}

/** Promotes a hand-move into a real constraint — an agreed delivery date, say. */
export function useFixPlanMove(batchId: string) {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (stationKey: StationKey) =>
      post<Batch>(`/batches/${batchId}/plan/fix`, { stationKey }),
    onSuccess: invalidate,
  });
}

export function usePinBooking() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
      patch(`/bookings/${id}`, { pinned }),
    onSuccess: invalidate,
  });
}

/** Pins or unpins a whole job's plan in one call. */
export function useSetJobPinned(jobId: string) {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (pinned: boolean) => patch(`/jobs/${jobId}/bookings`, { pinned }),
    onSuccess: invalidate,
  });
}

export function useSetStep(batchId: string) {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ stationKey, status }: { stationKey: StationKey; status: StepStatus }) =>
      patch(`/batches/${batchId}/steps/${stationKey}`, { status }),
    onSuccess: invalidate,
  });
}

function usePartsInvalidation(batchId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.parts(batchId) });
    void qc.invalidateQueries({ queryKey: keys.labels(batchId) });
    void qc.invalidateQueries({ queryKey: ['job'] });
  };
}

export function useCreatePart(batchId: string) {
  return useMutation({
    mutationFn: (input: PartCreate) => post(`/batches/${batchId}/parts`, input),
    onSuccess: usePartsInvalidation(batchId),
  });
}

export function usePatchPart(batchId: string) {
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PartPatch }) => patch(`/parts/${id}`, input),
    onSuccess: usePartsInvalidation(batchId),
  });
}

export function useDeletePart(batchId: string) {
  return useMutation({
    mutationFn: (id: string) => del<void>(`/parts/${id}`),
    onSuccess: usePartsInvalidation(batchId),
  });
}

export function useAddComponent(batchId: string) {
  return useMutation({
    mutationFn: ({ partId, name }: { partId: string; name: string }) =>
      post(`/parts/${partId}/components`, { name }),
    onSuccess: usePartsInvalidation(batchId),
  });
}

export function useRemoveComponent(batchId: string) {
  return useMutation({
    mutationFn: ({ partId, code }: { partId: string; code: string }) =>
      del(`/parts/${partId}/components/${code}`),
    onSuccess: usePartsInvalidation(batchId),
  });
}

export function useRecordScan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ScanCreate) => post<ScanResult>('/scans', input),
    // Recording from the Scans page puts the scan at the top of the list behind the
    // manual-scan panel, so it is there when the panel closes.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scans', 'recent'] });
    },
  });
}

function useAdminInvalidation(key: readonly unknown[]) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: key });
    // Capacity is derived from these three collections, so the board changes too.
    void qc.invalidateQueries({ queryKey: ['board'] });
  };
}

export function useSaveUser() {
  return useMutation({
    mutationFn: (input: UserWrite) => put<User>('/users', input),
    onSuccess: useAdminInvalidation(keys.users),
  });
}

export function useDeleteUser() {
  return useMutation({
    mutationFn: (id: string) => del<void>(`/users/${id}`),
    onSuccess: useAdminInvalidation(keys.users),
  });
}

export function useSaveStation() {
  return useMutation({
    mutationFn: (input: Partial<Station> & { key: StationKey; name: string }) =>
      put<Station>('/stations', input),
    onSuccess: useAdminInvalidation(keys.stations),
  });
}

export function useDeleteStation() {
  return useMutation({
    mutationFn: (key: StationKey) => del<void>(`/stations/${key}`),
    onSuccess: useAdminInvalidation(keys.stations),
  });
}

export function useSaveWorker() {
  return useMutation({
    mutationFn: (input: WorkerWrite) => post<Worker>('/workers', input),
    onSuccess: useAdminInvalidation(keys.workers),
  });
}

export function useDeleteWorker() {
  return useMutation({
    mutationFn: (id: string) => del<void>(`/workers/${id}`),
    onSuccess: useAdminInvalidation(keys.workers),
  });
}

export function useSaveTemplate() {
  return useMutation({
    mutationFn: (input: JobTemplateWrite) => put<JobTemplate>('/job-templates', input),
    onSuccess: useAdminInvalidation(keys.templates),
  });
}

export function useDeleteTemplate() {
  return useMutation({
    mutationFn: (name: string) => del<void>(`/job-templates/${encodeURIComponent(name)}`),
    onSuccess: useAdminInvalidation(keys.templates),
  });
}

export function useSaveAppearance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AppearanceWrite) => put<Appearance>('/appearance', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.appearance }),
  });
}

export function useSaveSchedulingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SchedulingSettingsWrite) =>
      put<SchedulingSettings>('/scheduling-settings', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.schedulingSettings }),
  });
}

export function useSaveException() {
  return useMutation({
    mutationFn: (input: CalendarExceptionWrite) => post('/calendar-exceptions', input),
    onSuccess: useAdminInvalidation(keys.exceptions),
  });
}

export function useDeleteException() {
  return useMutation({
    mutationFn: (id: string) => del<void>(`/calendar-exceptions/${id}`),
    onSuccess: useAdminInvalidation(keys.exceptions),
  });
}
